/**
 * Design-system bridge. Imports the app's REAL semantic tokens
 * (`constants/theme/tokens/semantic`) - the same layer the product consumes -
 * and emits them as CSS variables so the portal is visually identical to
 * AssetWRX: ink-and-paper enterprise (brand ink primary, signal-amber spark,
 * cool-slate neutrals). Light + dark, following `prefers-color-scheme`.
 *
 * This is pure token data (no react-native), so it bundles cleanly.
 */
import { lightSemantic, darkSemantic } from '@/constants/theme/tokens/semantic';

// A union, not just the light shape: the two token objects have identical
// structure but different string literal types, so `typeof lightSemantic` alone
// rejects the dark one under strict checking.
type Semantic = typeof lightSemantic | typeof darkSemantic;

function toVars(s: Semantic): Record<string, string> {
  return {
    '--bg': s.surface.canvas,
    '--panel': s.surface.raised,
    '--panel2': s.surface.sunken,
    '--panel-hover': s.surface.hover,
    '--border': s.border.subtle,
    '--border-default': s.border.default,
    '--border-strong': s.border.strong,
    '--border-brand': s.border.brand,
    '--text': s.text.primary,
    '--muted': s.text.secondary,
    '--muted2': s.text.tertiary,
    '--text-inverse': s.text.inverse,
    '--on-brand': s.text.onBrand,
    '--primary': s.surface.brand.solid,
    '--primary-hover': s.surface.brand.solidHover,
    '--primary-subtle': s.surface.brand.subtle,
    '--brand-text': s.text.brand,
    '--accent': s.focus.ring, // signal amber - the spark (selection / explicit / focus)
    '--selected': s.surface.selected,
    '--on': s.text.status.success,
    '--on-solid': s.surface.status.success.solid,
    '--on-subtle': s.surface.status.success.subtle,
    '--off': s.text.tertiary,
    '--warn': s.text.status.warning,
    '--warn-subtle': s.surface.status.warning.subtle,
    '--danger': s.text.status.danger,
    '--danger-subtle': s.surface.status.danger.subtle,
    '--code-bg': s.code.bg,
    '--code-text': s.code.text,
    '--overlay': s.surface.overlay,
  };
}

function block(selector: string, vars: Record<string, string>): string {
  const body = Object.entries(vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
  return `${selector} {\n${body}\n}`;
}

/** Injects the AssetWRX token CSS variables (light default + dark via media query). */
export function injectTheme(): void {
  const light = toVars(lightSemantic);
  const dark = toVars(darkSemantic);
  const css = [
    block(':root', light),
    `@media (prefers-color-scheme: dark) {\n${block(':root', dark)}\n}`,
  ].join('\n\n');
  const style = document.createElement('style');
  style.id = 'aw-theme';
  style.textContent = css;
  document.head.appendChild(style);
}
